import React, { useEffect, useState } from "react"
import { Select, Tooltip } from "antd"
import { CheckOutlined, PlusOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faShuffle,
  faWandMagicSparkles,
  faTerminal,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons"
import { Editor } from "@tiptap/react"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { escapeHTML } from "utils/escapeHtml"
import { useProjectStore, Project } from "stores/projectStore"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import { useQueryBuilderStore } from "stores/queryBuilderStore"
import { SavedQueryEnvelope, SavedQueryKind } from "types/savedQuery.types"

// Define the option structure for the Select component
interface SelectOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

/**
 * Inline icon used to label each saved query in the picker by kind. Pipelines
 * get the shuffle (Transform) icon; builders the wand; scripts the terminal.
 * Unknown kinds (legacy or corrupt) render a question mark so the user can
 * see something's off without losing access to the record.
 */
const KindIcon: React.FC<{ kind: SavedQueryKind | "unknown" }> = ({ kind }) => {
  let icon = faTerminal
  let title = "Script"
  let color = "var(--qc-ink-muted, #6b7280)"
  if (kind === "pipeline") {
    icon = faShuffle
    title = "Pipeline (opens in Transform)"
    color = "var(--qc-accent, var(--dq-brand))"
  } else if (kind === "builder") {
    icon = faWandMagicSparkles
    title = "Builder (opens in Builder)"
    color = "var(--qc-accent, var(--dq-brand))"
  } else if (kind === "unknown") {
    icon = faCircleQuestion
    title = "Unknown kind"
    color = "var(--qc-error, #a8412c)"
  }
  return (
    <Tooltip title={title} placement="left">
      <FontAwesomeIcon
        icon={icon}
        style={{ color, fontSize: 11, width: 12, flexShrink: 0 }}
        aria-label={title}
      />
    </Tooltip>
  )
}

// Define the props interface for the QueryStatus component
interface QueryStatusProps {
  /** Array of saved query names */
  projectQueries: string[]
  /** Currently selected query name */
  selectedQuery: string
  /** TipTap editor instance */
  editor: Editor | null
  /** Function to update content changed state */
  setContentChanged: (changed: boolean) => void
  /** Function to update selected query */
  setSelectedQuery: (query: string) => void
}

/**
 * QueryStatus component displays the current query selection status and provides
 * a dropdown to switch between saved queries or create a new one.
 *
 * @component
 * @param props - The component props
 * @returns The rendered QueryStatus component
 */
const QueryStatus: React.FC<QueryStatusProps> = ({
  projectQueries,
  selectedQuery,
  editor,
  setContentChanged,
  setSelectedQuery,
}) => {
  const project: Project = useProjectStore((state) => state.project)
  const { getQuery, getStructured, listStructured } = useSavedQueries()
  const setViewMode = useQueryConsoleLayoutStore((s) => s.setViewMode)
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const setConsoleStructuredOrigin = useQueryConsoleLayoutStore(
    (s) => s.setConsoleStructuredOrigin
  )

  // What the Select renders depends on the current viewMode:
  //   - console: the editor-loaded script's name (`selectedQuery` prop)
  //   - transform: the active pipeline's saved name (if its in-store name
  //     still matches what was last persisted to the backend)
  //   - builder: the active builder's saved name
  // The store-derived values are reactive selectors so the picker stays in
  // sync when the user (de)activates pipelines/builders elsewhere.
  const activePipelineSavedName = useQueryBuilderStore((s) => {
    const ap = s.transformPipelines.find(
      (p) => p.id === s.activeTransformPipelineId
    )
    if (!ap) return ""
    return s.pipelineSavedNames[ap.id] === ap.name ? ap.name : ""
  })
  const activeBuilderSavedName = useQueryBuilderStore((s) => {
    const ab = s.builders.find((b) => b.id === s.activeBuilderId)
    if (!ab) return ""
    return s.builderSavedNames[ab.id] === ab.name ? ab.name : ""
  })
  const displayedSelected =
    viewMode === "transform"
      ? activePipelineSavedName
      : viewMode === "builder"
      ? activeBuilderSavedName
      : selectedQuery

  // Cache of envelope kind by name. Lazy-loaded once per project; refreshes
  // when the project's query list changes. Used purely for decorating the
  // Select options — fetch failures degrade silently to "no icon".
  const [kindByName, setKindByName] = useState<Map<string, SavedQueryKind>>(
    new Map()
  )
  useEffect(() => {
    let cancelled = false
    if (!project?.id || !project?.Created_by) return
    listStructured(project.id, project.Created_by)
      .then((envelopes: SavedQueryEnvelope[]) => {
        if (cancelled) return
        const next = new Map<string, SavedQueryKind>()
        envelopes.forEach((env) => next.set(env.name, env.kind))
        setKindByName(next)
      })
      .catch(() => {
        // ignore — we just won't show icons
      })
    return () => {
      cancelled = true
    }
  }, [project?.id, project?.Created_by, projectQueries, listStructured])

  /**
   * Handles the selection of a saved query from the dropdown.
   *
   * Kind-aware routing:
   *  - script   → load into the Console editor (existing behaviour)
   *  - pipeline → switch to Transform tab and activate that pipeline
   *  - builder  → switch to Builder tab and activate that builder
   *
   * Falls back to the editor-load path on any envelope-fetch error so the
   * user is never left without a way to open the record.
   */
  const handleSelectSavedQuery = async (
    e: unknown,
    item: string
  ): Promise<void> => {
    if (!editor) return

    if (item === "create_new") {
      handleCreateNew()
      return
    }

    let kind: SavedQueryKind = "script"
    try {
      const env = await getStructured(project.id, project.Created_by, item)
      kind = env.kind
    } catch (err) {
      console.warn(
        "QueryStatus: kind probe failed, falling back to script:",
        err
      )
    }

    // When the user is already in Console mode, picking a structured record
    // loads the underlying script as read-only text rather than yanking them
    // off to the Transform/Builder tab. The origin marker drives the banner
    // and routes Save through "Save as new script" to preserve the original
    // structured record.
    if (viewMode === "console" && (kind === "pipeline" || kind === "builder")) {
      try {
        const queryRes = await getQuery(project.id, project.Created_by, item)
        const content = queryRes.data
          .split("\n")
          .map((line: string) => `<p>${escapeHTML(line)}</p>`)
          .join("")
        editor.commands.clearContent()
        editor.commands.insertContent(content)
        setSelectedQuery(item)
        setContentChanged(false)
        setConsoleStructuredOrigin({ name: item, kind })
      } catch (error) {
        console.error("Error loading structured record into Console:", error)
        throw error
      }
      return
    }

    if (kind === "pipeline") {
      const state = useQueryBuilderStore.getState()
      const pipeline = state.transformPipelines.find((p) => p.name === item)
      if (pipeline) state.setActiveTransformPipeline(pipeline.id)
      // Don't touch `selectedQuery`: that tracks the Console editor's loaded
      // script, not the active pipeline. The QueryStatus display derives
      // what's selected per-viewMode from the store directly.
      setConsoleStructuredOrigin(null)
      setViewMode("transform")
      return
    }
    if (kind === "builder") {
      const state = useQueryBuilderStore.getState()
      const builder = state.builders.find((b) => b.name === item)
      if (builder) state.setActiveBuilder(builder.id)
      setConsoleStructuredOrigin(null)
      setViewMode("builder")
      return
    }

    try {
      const queryRes = await getQuery(project.id, project.Created_by, item)
      editor.commands.clearContent()
      const content = queryRes.data
        .split("\n")
        .map((line: string) => `<p>${escapeHTML(line)}</p>`)
        .join("")
      editor.commands.insertContent(content)
      setSelectedQuery(item)
      setContentChanged(false)
      // A plain script always opens straight into the editor; clear any
      // lingering structured-origin banner state from a prior pipeline view.
      setConsoleStructuredOrigin(null)
      // Make the editor visible — picking a script from any non-console
      // view should land the user in Console where the script just got
      // loaded, otherwise the action seems to do nothing.
      setViewMode("console")
    } catch (error) {
      console.error(`Error in API call:`, error)
      throw error
    }
  }

  /**
   * "New" is viewMode-aware: in Console it clears the editor, in Transform it
   * creates and activates a fresh pipeline, in Builder a fresh builder. Saved
   * routes back to Console with a clean editor so the user has somewhere to
   * write. Without this branching, clicking New from Transform/Builder did
   * nothing because the editor isn't visible there.
   */
  const handleCreateNew = (): void => {
    if (viewMode === "transform") {
      const state = useQueryBuilderStore.getState()
      const id = state.createTransformPipeline()
      state.setActiveTransformPipeline(id)
      return
    }
    if (viewMode === "builder") {
      const state = useQueryBuilderStore.getState()
      const id = state.createBuilder()
      state.setActiveBuilder(id)
      return
    }
    if (!editor) return
    editor.commands.clearContent()
    setSelectedQuery("")
    // A blank canvas — drop any structured-origin marker so the banner
    // doesn't keep referencing the previous record.
    setConsoleStructuredOrigin(null)
    if (viewMode === "saved") {
      // Saved is a list view; redirect the user to where the new editor
      // surface actually is.
      setViewMode("console")
    }
  }

  // Build the options array for the Select component
  // In Transform/Builder mode the user can always spin up another
  // pipeline/builder — gating "New" on a current selection only makes sense
  // for the editor-centric Console flow.
  const newDisabled =
    viewMode === "transform" || viewMode === "builder"
      ? false
      : !displayedSelected

  const allOptions: SelectOption[] = [
    {
      value: "create_new",
      label: (
        <div
          className={`flex justify-between items-center ${
            newDisabled
              ? "opacity-50 cursor-not-allowed"
              : "text-[var(--dq-brand)] hover:text-[var(--dq-brand-hover)]"
          }`}
        >
          <span>New</span>
          <PlusOutlined />
        </div>
      ),
      disabled: newDisabled,
    },
    ...(projectQueries?.map(
      (query: string): SelectOption => ({
        value: query,
        label: (
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-2 min-w-0">
              <KindIcon kind={kindByName.get(query) ?? "script"} />
              <span className="truncate">{query}</span>
            </span>
            {query === displayedSelected && (
              <CheckOutlined className="text-[var(--dq-brand)]" />
            )}
          </div>
        ),
      })
    ) || []),
  ]

  return (
    <div className="flex min-w-0">
      <Select
        aria-label="Saved query"
        value={displayedSelected || undefined}
        placeholder="Saved query..."
        // Shrinks (down to 96px) when the toolbar is narrow.
        style={{ width: 180, minWidth: 96 }}
        options={allOptions}
        onSelect={(value: string) => handleSelectSavedQuery(null, value)}
      />
    </div>
  )
}

export default QueryStatus
